import axiosInstance from "./axiosInstance";

function authHeader(token?: string) {
    if (!token || token.trim().length === 0) return {};
    return { Authorization: `Bearer ${token}` };
}

/** Well-known setting keys. */
export const DEMO_VIDEO_URL_KEY = "demo_video_url";
/** JSON array of {title, url} — the demo videos shown at /demo, in order.
 *  Supersedes the single demo_video_url (still read as a fallback). */
export const DEMO_VIDEOS_KEY = "demo_videos";
/** Email address notified when a new account registers (unset = no email). */
export const SIGNUP_NOTIFICATION_EMAIL_KEY = "signup_notification_email";
/** "false" disables the strict registration-email policy (blocks '+', etc.).
 *  Absent or any other value = enabled. */
export const EMAIL_STRICT_LOCAL_PART_KEY = "email_strict_local_part";
/** Upload limit in MB for listing photos/HTML docs (absent = 10). Enforced
 *  live at the gateway; the servers' 25MB multipart ceiling is the max. */
export const UPLOAD_MAX_FILE_MB_KEY = "upload_max_file_mb";

/** Reads a site setting; null when not set. Reads are public. */
export async function getSetting(key: string): Promise<string | null> {
    try {
        const resp = await axiosInstance.get<{ key: string; value: string }>(`/settings/${key}`);
        return resp.data?.value ?? null;
    } catch (e: any) {
        if (e?.response?.status === 404) return null;
        throw e;
    }
}

/** Sets or changes a site setting (ROOT / SYS_ADMIN). */
export async function putSetting(token: string, key: string, value: string): Promise<void> {
    await axiosInstance.put(`/settings/${key}`, { value }, { headers: authHeader(token) });
}

/** Removes a site setting (ROOT / SYS_ADMIN). */
export async function deleteSetting(token: string, key: string): Promise<void> {
    await axiosInstance.delete(`/settings/${key}`, { headers: authHeader(token) });
}

/** One demo video: a YouTube link plus the heading shown above the player.
 *  The slug makes the video directly linkable at /demo/{slug}. */
export interface DemoVideo {
    title: string;
    url: string;
    slug?: string;
}

/** URL slug from a title: "Home Rental!" -> "home_rental". Empty when the
 *  title has nothing usable. */
export function slugify(title: string): string {
    return title
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}

/** The demo-video list, in display order. Falls back to the legacy single
 *  demo_video_url setting so existing sites keep their video until the list
 *  is saved once. A malformed stored value yields [] rather than breaking
 *  the public page. */
export async function getDemoVideos(): Promise<DemoVideo[]> {
    const raw = await getSetting(DEMO_VIDEOS_KEY);
    if (raw) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed
                    .filter((v) => v && typeof v.url === "string" && v.url.trim())
                    .map((v) => ({
                        title: String(v.title ?? ""),
                        url: String(v.url),
                        slug: typeof v.slug === "string" && v.slug ? v.slug : undefined,
                    }));
            }
        } catch {
            return [];
        }
        return [];
    }
    const legacy = await getSetting(DEMO_VIDEO_URL_KEY);
    return legacy ? [{ title: "", url: legacy }] : [];
}

/** Saves the whole demo-video list (ROOT / SYS_ADMIN). */
export async function saveDemoVideos(token: string, videos: DemoVideo[]): Promise<void> {
    await putSetting(token, DEMO_VIDEOS_KEY, JSON.stringify(videos));
}

export type RecipientVerificationStatus = "NOT_REQUESTED" | "PENDING" | "VERIFIED";

/** Sandboxed SES (local/dev) can only deliver to verified recipients; this asks
 *  AWS to email the address its verification link. ROOT / SYS_ADMIN. */
export async function requestRecipientVerification(token: string, email: string): Promise<void> {
    await axiosInstance.post("/settings/verify-recipient", { email }, { headers: authHeader(token) });
}

export async function getRecipientVerificationStatus(
    token: string,
    email: string
): Promise<RecipientVerificationStatus> {
    const resp = await axiosInstance.get<{ status: RecipientVerificationStatus }>(
        `/settings/verify-recipient/${encodeURIComponent(email)}`,
        { headers: authHeader(token) }
    );
    return resp.data.status;
}

/** Normalizes a YouTube link (youtu.be/…, watch?v=…, shorts/…) to an embed URL.
 *  Returns null when the URL isn't recognizable as YouTube. */
export function toYouTubeEmbedUrl(url: string): string | null {
    try {
        const u = new URL(url.trim());
        const host = u.hostname.replace(/^www\./, "");
        let id = "";
        if (host === "youtu.be") {
            id = u.pathname.slice(1).split("/")[0];
        } else if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "m.youtube.com") {
            if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] ?? "";
            else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/")[2] ?? "";
            else id = u.searchParams.get("v") ?? "";
        }
        if (!id) return null;
        return `https://www.youtube-nocookie.com/embed/${id}`;
    } catch {
        return null;
    }
}